/**
 * Customer-scoped phone-recording settings page. Combines the two
 * customer-level cards under one URL so all three relevant roles have a
 * single place to manage everything related to phone-recording for the
 * customer they're scoped to.
 *
 * Roles + scope:
 *   - super_admin / reseller_owner: scope comes from the customer-context
 *     store (the "Enter customer" pattern). Without an entered customer
 *     they see a "pick a customer first" message.
 *   - office_manager: scope is their own customer (from JWT tenant). They
 *     never need to enter a customer — they ARE one.
 *
 * Cards rendered:
 *   1. PhoneRecordingSettingsCard — hangup auto-save vs discard + recycle
 *      bin retention slider.
 *   2. RecordingAdminsCard — register + verify phone numbers allowed to
 *      record.
 */
import { useQuery } from '@tanstack/react-query';
import { customers } from '@/api/customers';
import { PageHeader } from '@/components/PageHeader';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { RecordingAdminsCard } from '@/components/recordingAdmins/RecordingAdminsCard';
import { PhoneRecordingSettingsCard } from '@/components/recordingAdmins/PhoneRecordingSettingsCard';
import { PhoneAdminConsoleCard } from '@/components/recordingAdmins/PhoneAdminConsoleCard';

export function RecordingAdminsPage() {
  const customerId = useEffectiveCustomerId();

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => (customerId ? customers.get(customerId) : Promise.reject(new Error('no_customer'))),
    enabled: Boolean(customerId),
  });

  if (!customerId) {
    return (
      <>
        <PageHeader
          eyebrow="Settings"
          title="Phone recording"
          description="Configure hangup behaviour, recycle-bin retention, and which phone numbers are authorised to record."
        />
        <div className="text-sm text-muted-foreground">
          Open a customer from the Customers list first (super_admin /
          reseller_owner). The page will then show settings for that customer.
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Phone recording"
        description="Configure hangup behaviour, recycle-bin retention, and which phone numbers are authorised to record."
      />

      <div className="space-y-6">
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}
        {error && (
          <div role="alert" className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load settings'}
          </div>
        )}
        {customer && <PhoneRecordingSettingsCard customer={customer} />}
        {customer && <PhoneAdminConsoleCard customer={customer} />}
        <RecordingAdminsCard customerId={customerId} />
      </div>
    </>
  );
}
